// Strings for the Texas Capital admin configuration screen (FC2-3553 S2)
// and the per-IG status indicators in the funding flow (FC2-3553 S5 partial).
// Both EN and ES are provided; webui defaults to the browser locale and falls
// back to EN. Keep keys prefixed with TEXAS_CAPITAL_ to avoid collisions.
export const texasCapitalEn = {
  TEXAS_CAPITAL: 'Texas Capital',
  TEXAS_CAPITAL_TITLE: 'Texas Capital Configuration',
  TEXAS_CAPITAL_SUBTITLE:
    'Configure OAuth credentials and the debtor account used as the source of funds for instant payments.',
  TEXAS_CAPITAL_HUB_DESCRIPTION:
    'Embedded banking integration for instant payments. Configure credentials and debtor account.',
  TEXAS_CAPITAL_CONFIGURE: 'Configure',
  TEXAS_CAPITAL_ENABLE: 'Enable Texas Capital',
  TEXAS_CAPITAL_ENABLED: 'Enabled',
  TEXAS_CAPITAL_DISABLED: 'Disabled',
  TEXAS_CAPITAL_AUTHORIZED: 'Authorized',
  TEXAS_CAPITAL_NOT_AUTHORIZED: 'Not authorized yet',

  TEXAS_CAPITAL_SECTION_CREDENTIALS: 'Credentials',
  TEXAS_CAPITAL_SECTION_DEBTOR: 'Debtor (source of funds)',

  TEXAS_CAPITAL_CLIENT_ID: 'Client ID',
  TEXAS_CAPITAL_CLIENT_SECRET: 'Client Secret',
  TEXAS_CAPITAL_CLIENT_SECRET_PLACEHOLDER: 'Leave blank to keep current value',
  TEXAS_CAPITAL_CLIENT_SECRET_SET: 'Stored — leave blank to keep',
  TEXAS_CAPITAL_DEBTOR_NAME: 'Debtor name',
  TEXAS_CAPITAL_DEBTOR_ACCOUNT_NUMBER: 'Debtor account number',
  TEXAS_CAPITAL_DEBTOR_ROUTING_NUMBER: 'Debtor routing number',

  TEXAS_CAPITAL_ERROR_CLIENT_ID_REQUIRED: 'Client ID is required when Texas Capital is enabled',
  TEXAS_CAPITAL_ERROR_CLIENT_ID_MAX: 'Client ID must be 200 characters or fewer',
  TEXAS_CAPITAL_ERROR_DEBTOR_NAME_REQUIRED: 'Debtor name is required when Texas Capital is enabled',
  TEXAS_CAPITAL_ERROR_DEBTOR_NAME_MAX: 'Debtor name must be 140 characters or fewer',
  TEXAS_CAPITAL_ERROR_DEBTOR_ACCOUNT_REQUIRED: 'Debtor account number is required when Texas Capital is enabled',
  TEXAS_CAPITAL_ERROR_DEBTOR_ACCOUNT_DIGITS: 'Account number must contain digits only (max 17)',
  TEXAS_CAPITAL_ERROR_DEBTOR_ROUTING_REQUIRED: 'Routing number is required when Texas Capital is enabled',
  TEXAS_CAPITAL_ERROR_DEBTOR_ROUTING_DIGITS: 'Routing number must be exactly 9 digits',

  TEXAS_CAPITAL_SAVE: 'Save',
  TEXAS_CAPITAL_SAVE_SUCCESS: 'Texas Capital configuration saved',
  TEXAS_CAPITAL_SAVE_ERROR: 'Failed to save Texas Capital configuration',

  TEXAS_CAPITAL_TEST_AUTH: 'Test Authentication',
  TEXAS_CAPITAL_TEST_AUTH_SUCCESS: 'Authentication successful',
  TEXAS_CAPITAL_TEST_AUTH_TOKEN_EXPIRES: 'Token expires {date}',
  TEXAS_CAPITAL_TEST_AUTH_FAILED: 'Authentication failed',
  TEXAS_CAPITAL_TEST_AUTH_HINT: 'Save credentials before testing.',

  TEXAS_CAPITAL_FAKE_LOGIN: 'Fake Login (testing only)',
  TEXAS_CAPITAL_FAKE_LOGIN_HINT:
    'Bypasses the bank in non-prod environments where fake-auth is enabled. Mints a synthetic token so the funding flow can run without live TCB credentials.',
  TEXAS_CAPITAL_FAKE_LOGIN_SUCCESS: 'Fake authorized — bank was not contacted',
  TEXAS_CAPITAL_FAKE_LOGIN_NOT_AVAILABLE:
    'Fake login not available in this environment — set integration.texascapital.fake-auth.enabled=true on the server',
  TEXAS_CAPITAL_FAKE_LOGIN_FAILED: 'Fake login failed',

  TEXAS_CAPITAL_COPY_FROM_CHECK: 'Copy from Check Payable',
  TEXAS_CAPITAL_COPY_FROM_CHECK_TOOLTIP:
    'Pre-fill debtor name, account number, and routing from the factor’s Check Payable info',
  TEXAS_CAPITAL_COPY_FROM_CHECK_DONE: 'Copied from Check Payable info',
  TEXAS_CAPITAL_COPY_FROM_CHECK_NOTHING:
    'No Check Payable values found on this factor to copy',

  TEXAS_CAPITAL_SUPER_ADMIN_ONLY_NOTICE: 'This integration is super-admin only.',

  TEXAS_CAPITAL_LOADING: 'Loading Texas Capital configuration…',

  // S5 partial — per-IG status indicators in the disbursement details list.
  TEXAS_CAPITAL_PROCESSING: 'Processing',
  TEXAS_CAPITAL_COMPLETED: 'Completed',
  // FedWire IMAD/OMAD surfaced on settled wires for reconciliation.
  TEXAS_CAPITAL_FED_REF_LABEL: 'Fed Ref',
  TEXAS_CAPITAL_FED_REF_COPY_TOOLTIP: 'Copy FedWire reference',
  TEXAS_CAPITAL_FED_REF_COPIED: 'FedWire reference copied',
  TEXAS_CAPITAL_RETRY_CONFIRMATION:
    'Are you sure you want to retry this Texas Capital payment? A new instruction will be sent to the bank.',
  TEXAS_CAPITAL_RETRY_SUCCESSFULLY: 'Texas Capital payment retry queued',
}

export const texasCapitalEs: typeof texasCapitalEn = {
  TEXAS_CAPITAL: 'Texas Capital',
  TEXAS_CAPITAL_TITLE: 'Configuración de Texas Capital',
  TEXAS_CAPITAL_SUBTITLE:
    'Configure las credenciales OAuth y la cuenta deudora utilizada como fuente de fondos para pagos instantáneos.',
  TEXAS_CAPITAL_HUB_DESCRIPTION:
    'Integración bancaria embebida para pagos instantáneos. Configure credenciales y cuenta deudora.',
  TEXAS_CAPITAL_CONFIGURE: 'Configurar',
  TEXAS_CAPITAL_ENABLE: 'Habilitar Texas Capital',
  TEXAS_CAPITAL_ENABLED: 'Habilitado',
  TEXAS_CAPITAL_DISABLED: 'Deshabilitado',
  TEXAS_CAPITAL_AUTHORIZED: 'Autorizado',
  TEXAS_CAPITAL_NOT_AUTHORIZED: 'Aún no autorizado',

  TEXAS_CAPITAL_SECTION_CREDENTIALS: 'Credenciales',
  TEXAS_CAPITAL_SECTION_DEBTOR: 'Deudor (fuente de fondos)',

  TEXAS_CAPITAL_CLIENT_ID: 'Client ID',
  TEXAS_CAPITAL_CLIENT_SECRET: 'Client Secret',
  TEXAS_CAPITAL_CLIENT_SECRET_PLACEHOLDER: 'Dejar en blanco para mantener el valor actual',
  TEXAS_CAPITAL_CLIENT_SECRET_SET: 'Almacenado — dejar en blanco para mantenerlo',
  TEXAS_CAPITAL_DEBTOR_NAME: 'Nombre del deudor',
  TEXAS_CAPITAL_DEBTOR_ACCOUNT_NUMBER: 'Número de cuenta del deudor',
  TEXAS_CAPITAL_DEBTOR_ROUTING_NUMBER: 'Número de ruta del deudor',

  TEXAS_CAPITAL_ERROR_CLIENT_ID_REQUIRED: 'El Client ID es obligatorio cuando Texas Capital está habilitado',
  TEXAS_CAPITAL_ERROR_CLIENT_ID_MAX: 'El Client ID debe tener 200 caracteres o menos',
  TEXAS_CAPITAL_ERROR_DEBTOR_NAME_REQUIRED:
    'El nombre del deudor es obligatorio cuando Texas Capital está habilitado',
  TEXAS_CAPITAL_ERROR_DEBTOR_NAME_MAX: 'El nombre del deudor debe tener 140 caracteres o menos',
  TEXAS_CAPITAL_ERROR_DEBTOR_ACCOUNT_REQUIRED:
    'El número de cuenta del deudor es obligatorio cuando Texas Capital está habilitado',
  TEXAS_CAPITAL_ERROR_DEBTOR_ACCOUNT_DIGITS: 'El número de cuenta debe contener solo dígitos (máx. 17)',
  TEXAS_CAPITAL_ERROR_DEBTOR_ROUTING_REQUIRED:
    'El número de ruta es obligatorio cuando Texas Capital está habilitado',
  TEXAS_CAPITAL_ERROR_DEBTOR_ROUTING_DIGITS: 'El número de ruta debe tener exactamente 9 dígitos',

  TEXAS_CAPITAL_SAVE: 'Guardar',
  TEXAS_CAPITAL_SAVE_SUCCESS: 'Configuración de Texas Capital guardada',
  TEXAS_CAPITAL_SAVE_ERROR: 'No se pudo guardar la configuración de Texas Capital',

  TEXAS_CAPITAL_TEST_AUTH: 'Probar autenticación',
  TEXAS_CAPITAL_TEST_AUTH_SUCCESS: 'Autenticación exitosa',
  TEXAS_CAPITAL_TEST_AUTH_TOKEN_EXPIRES: 'El token vence {date}',
  TEXAS_CAPITAL_TEST_AUTH_FAILED: 'La autenticación falló',
  TEXAS_CAPITAL_TEST_AUTH_HINT: 'Guarde las credenciales antes de probar.',

  TEXAS_CAPITAL_FAKE_LOGIN: 'Login simulado (solo testing)',
  TEXAS_CAPITAL_FAKE_LOGIN_HINT:
    'Omite el banco en entornos no productivos donde fake-auth está habilitado. Genera un token sintético para que el flujo de fondeo pueda ejecutarse sin credenciales reales de TCB.',
  TEXAS_CAPITAL_FAKE_LOGIN_SUCCESS: 'Autorización simulada — no se contactó al banco',
  TEXAS_CAPITAL_FAKE_LOGIN_NOT_AVAILABLE:
    'Login simulado no disponible en este entorno — habilítelo con integration.texascapital.fake-auth.enabled=true en el servidor',
  TEXAS_CAPITAL_FAKE_LOGIN_FAILED: 'El login simulado falló',

  TEXAS_CAPITAL_COPY_FROM_CHECK: 'Copiar de Check Payable',
  TEXAS_CAPITAL_COPY_FROM_CHECK_TOOLTIP:
    'Prerellena nombre del deudor, número de cuenta y ruta desde la información de Check Payable del factor',
  TEXAS_CAPITAL_COPY_FROM_CHECK_DONE: 'Copiado desde la información de Check Payable',
  TEXAS_CAPITAL_COPY_FROM_CHECK_NOTHING:
    'Este factor no tiene valores de Check Payable para copiar',

  TEXAS_CAPITAL_SUPER_ADMIN_ONLY_NOTICE: 'Esta integración es solo para super-administradores.',

  TEXAS_CAPITAL_LOADING: 'Cargando configuración de Texas Capital…',

  // S5 partial — per-IG status indicators in the disbursement details list.
  TEXAS_CAPITAL_PROCESSING: 'Procesando',
  TEXAS_CAPITAL_COMPLETED: 'Completado',
  TEXAS_CAPITAL_FED_REF_LABEL: 'Ref. Fed',
  TEXAS_CAPITAL_FED_REF_COPY_TOOLTIP: 'Copiar referencia FedWire',
  TEXAS_CAPITAL_FED_REF_COPIED: 'Referencia FedWire copiada',
  TEXAS_CAPITAL_RETRY_CONFIRMATION:
    '¿Está seguro de que desea reintentar este pago de Texas Capital? Se enviará una nueva instrucción al banco.',
  TEXAS_CAPITAL_RETRY_SUCCESSFULLY: 'Reintento de pago Texas Capital encolado',
}
